import React, { useCallback, useContext, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import useBreadcrumbs from "../../utils/hooks/useBreadcrumbs";
import { MdLink } from "react-icons/md";
import AssociationRequestAPI from "../../services/api/AssociationRequestService";
import { NotifyContext } from "../../context/notifyContext";
import Alert from "../../UI/Alert/Alert";
import Button from "../../UI/Button/Button";

const STATUS_LABEL: Record<string, string> = {
  pending: "En attente",
  accepted: "Acceptée",
  rejected: "Rejetée",
};

const TYPE_LABEL: Record<string, string> = {
  parent_student: "Parent – Élève",
  tutor_student: "Tuteur – Élève",
};

export default function AssociationRequestDetails() {
  const { id } = useParams<{ id: string }>();
  const notify = useContext(NotifyContext);
  const [request, setRequest] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  useBreadcrumbs([
    { label: "Demandes d'association", icon: <MdLink />, is_link: true, href: "/app/association-requests" },
    { label: "Détail", is_link: false },
  ]);

  const fetchRequest = useCallback(() => {
    if (!id) return;
    setLoading(true);
    AssociationRequestAPI.get_request(Number(id))
      .then((res: any) => res?.data)
      .then((data: any) => {
        setRequest(data?.request);
        setError("");
      })
      .catch((err: any) => {
        setError(err?.message || err || "Erreur");
        notify(err?.message || err, "error");
      })
      .finally(() => setLoading(false));
  }, [id, notify]);

  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  const handleAccept = () => {
    if (!id) return;
    setActionLoading(true);
    AssociationRequestAPI.accept(Number(id))
      .then(() => {
        notify("Demande acceptée", "success");
        fetchRequest();
      })
      .catch((err: any) => notify(err?.message || err, "error"))
      .finally(() => setActionLoading(false));
  };

  const handleReject = () => {
    if (!id) return;
    setActionLoading(true);
    AssociationRequestAPI.reject(Number(id))
      .then(() => {
        notify("Demande rejetée", "success");
        fetchRequest();
      })
      .catch((err: any) => notify(err?.message || err, "error"))
      .finally(() => setActionLoading(false));
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }
  if (error || !request) {
    return (
      <div className="p-4">
        {error && <Alert color="error">{error}</Alert>}
        <Link to="/app/association-requests" className="text-primary-600 underline">
          Retour à la liste
        </Link>
      </div>
    );
  }

  const student = request.student;
  const parent = request.target_parent_id ? (request.targetParent ?? request.target_parent) : null;
  const tutor = request.target_tutor_id ? (request.targetTutor ?? request.target_tutor) : null;
  const person = parent || tutor;
  const isPending = request.status === "pending";

  return (
    <div className="p-4 max-w-2xl">
      <div className="bg-white rounded shadow p-6 space-y-4">
        <h2 className="text-xl font-semibold">Demande d'association #{request.id}</h2>
        <p>
          <span className="font-medium">Type :</span> {TYPE_LABEL[request.type] || request.type}
        </p>
        <p>
          <span className="font-medium">Statut :</span>{" "}
          <span
            className={
              request.status === "accepted"
                ? "text-green-700"
                : request.status === "rejected"
                ? "text-red-700"
                : "text-amber-700"
            }
          >
            {STATUS_LABEL[request.status] || request.status}
          </span>
        </p>
        <p>
          <span className="font-medium">Date :</span>{" "}
          {request.created_at
            ? new Date(request.created_at).toLocaleString()
            : "-"}
        </p>

        <div className="border-t pt-4 mt-4">
          <p className="font-medium mb-2">Élève</p>
          {student ? (
            <p>
              {student.first_name} {student.last_name}
              {student.user?.email && ` – ${student.user.email}`}
            </p>
          ) : (
            <p>-</p>
          )}
        </div>

        <div className="border-t pt-4">
          <p className="font-medium mb-2">
            {request.type === "parent_student" ? "Parent" : "Tuteur"}
          </p>
          {person ? (
            <p>
              {person.first_name} {person.last_name}
            </p>
          ) : (
            <p>-</p>
          )}
        </div>

        {request.message && (
          <div className="border-t pt-4">
            <p className="font-medium mb-2">Message</p>
            <p className="text-gray-600">{request.message}</p>
          </div>
        )}

        {isPending && (
          <div className="flex gap-3 pt-4 border-t">
            <Button
              color="primary"
              onClick={handleAccept}
              disabled={actionLoading}
            >
              Accepter
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={handleReject}
              disabled={actionLoading}
            >
              Rejeter
            </Button>
          </div>
        )}
      </div>
      <Link to="/app/association-requests" className="inline-block mt-4 text-primary-600 underline">
        Retour à la liste des demandes
      </Link>
    </div>
  );
}
